import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { useSelector } from 'react-redux'
import { RootState } from 'constants/interfaces'
import { hubViewsSelector } from 'redux/selectors/config'
import { useFormStepContext } from '../FormStepContext'
import { getInvalidSettings, scheduleFields, SettingsSection } from '../model/settings'

const EMPTY: string[] = []

/** Owns settings validation, list measurements and RHF v5 field updates; controls remain mounted in the view. */
export function useSettingsBuilder() {
  const { isVmTask, sumbitStep, isSubmitting, isMediaUploading } = useFormStepContext()
  const { control, watch, setValue, getValues, triggerValidation, errors } = useFormContext()

  const values = watch({ nest: true })

  const [active, setActive] = useState<SettingsSection | null>(() => (window.innerWidth >= 900 ? 'audience' : null))
  const [audienceType, setAudienceType] = useState<'groups' | 'users' | 'tags'>('groups')
  const [validation, setValidation] = useState({ key: '', valid: false })
  const [listHeight, setListHeight] = useState(360)

  const editorRef = useRef<HTMLDivElement>(null)

  const uid = useSelector((state: RootState) => state.auth.uid)
  const config = useSelector((state: RootState) => state.config.config)
  const permissions = useSelector((state: RootState) => state.config.userWriteTaskPermissions)

  const views = useSelector(hubViewsSelector) || []

  const allowedGroups = useMemo(() => Object.keys(permissions.groups || {}), [permissions.groups])
  const allowedUsers = useMemo(() => (permissions.users || []).filter((id) => id !== uid), [permissions.users, uid])

  const audience = values.audience || { groups: EMPTY, users: EMPTY, tags: EMPTY }
  const valueKey = JSON.stringify(values)
  const invalidSections = getInvalidSettings(values, errors, isVmTask)
  const canPublish =
    validation.key === valueKey && validation.valid && !invalidSections.length && !isMediaUploading && !isSubmitting

  const changeAudience = useCallback(
    (ids: string[]) => {
      setValue('audience', { ...getValues('audience'), [audienceType]: ids })
    },
    [audienceType, getValues, setValue]
  )

  const changeWatchers = useCallback(
    (ids: string[]) => setValue('watchers', ids.length ? ids : uid ? [uid] : []),
    [setValue, uid]
  )

  const closeSection = async (next: SettingsSection | null = null) => {
    const registered = Object.keys(getValues())

    if (active === 'schedule' && !(await triggerValidation(scheduleFields.filter((name) => registered.includes(name)))))
      return
    setActive(next)
  }

  // Validate the actual registered rules, including recurrence, even when its editor is hidden.
  useEffect(() => {
    let cancelled = false
    const timer = window.setTimeout(async () => {
      const valid = await triggerValidation()

      if (!cancelled) setValidation({ key: valueKey, valid })
    }, 100)

    return () => {
      cancelled = true
      window.clearTimeout(timer)
    }
  }, [valueKey, triggerValidation])

  useEffect(() => {
    const element = editorRef.current

    if (!element) return

    const resize = () => setListHeight(Math.max(160, element.clientHeight - (active === 'audience' ? 175 : 95)))

    resize()
    if (typeof ResizeObserver === 'undefined') return

    const observer = new ResizeObserver(resize)

    observer.observe(element)
    return () => observer.disconnect()
  }, [active])

  return {
    isVmTask,
    sumbitStep,
    isSubmitting,
    control,
    values,
    active,
    setActive,
    audienceType,
    setAudienceType,
    editorRef,
    listHeight,
    config,
    permissions,
    views,
    allowedGroups,
    allowedUsers,
    audience,
    invalidSections,
    canPublish,
    changeAudience,
    changeWatchers,
    closeSection,
  }
}
